import { useMutation, useQuery } from "@tanstack/react-query";
import { QUERY_CONFIGS } from "../../lib/react-query/config";
import bulkUploadsService from "../../services/api/bulkUploadsService";
import { POLL_INTERVAL_MS } from "../../components/BulkUploads/FileUpload/fileUploadConfig";

export const bulkUploadsQueryKeys = {
  all: ["bulkUploads"],
  bulkSummation: () => [...bulkUploadsQueryKeys.all, "bulkSummation"],
  batchHistory: (filters) => [
    ...bulkUploadsQueryKeys.all,
    "batchHistory",
    filters,
  ],
  batchDetail: (id) => [...bulkUploadsQueryKeys.all, "batchDetail", id],
  batchValidationPoll: (id) => [
    ...bulkUploadsQueryKeys.all,
    "batchValidationPoll",
    id,
  ],
  batchEvents: (id) => [...bulkUploadsQueryKeys.all, "batchEvents", id],
  batchRecipients: (bulkId, filters) => [
    ...bulkUploadsQueryKeys.all,
    "batchRecipients",
    bulkId,
    filters,
  ],
  batchRecipientsSummation: (bulkId) => [
    ...bulkUploadsQueryKeys.all,
    "batchRecipientsSummation",
    bulkId,
  ],
  batchValidationRecipients: (bulkId, filters) => [
    ...bulkUploadsQueryKeys.all,
    "batchValidationRecipients",
    bulkId,
    filters,
  ],
  manualEntryCharges: () => [...bulkUploadsQueryKeys.all, "manualEntryCharges"],
  createManualEntryBatch: () => [
    ...bulkUploadsQueryKeys.all,
    "createManualEntryBatch",
  ],
  disbursementLimits: (channel) => [
    ...bulkUploadsQueryKeys.all,
    "disbursementLimits",
    channel,
  ],
  hakikisha: () => [...bulkUploadsQueryKeys.all, "hakikisha"],
  uploadBulkFile: () => [...bulkUploadsQueryKeys.all, "uploadBulkFile"],
  updateBulkRecipient: () => [
    ...bulkUploadsQueryKeys.all,
    "updateBulkRecipient",
  ],
  approveBulkBatch: () => [...bulkUploadsQueryKeys.all, "approveBulkBatch"],
  bulkStatusFilters: () => [...bulkUploadsQueryKeys.all, "bulkStatusFilters"],
  bulkRecipientStatusFilters: () => [
    ...bulkUploadsQueryKeys.all,
    "bulkRecipientStatusFilters",
  ],
  managementBulkSummation: () => [
    ...bulkUploadsQueryKeys.all,
    "management",
    "summation",
  ],
  managementBatchHistory: (filters) => [
    ...bulkUploadsQueryKeys.all,
    "management",
    "batchHistory",
    filters,
  ],
  managementBatchDetail: (id) => [
    ...bulkUploadsQueryKeys.all,
    "management",
    "batchDetail",
    id,
  ],
  managementBatchEvents: (id) => [
    ...bulkUploadsQueryKeys.all,
    "management",
    "batchEvents",
    id,
  ],
  managementBatchRecipients: (id, filters) => [
    ...bulkUploadsQueryKeys.all,
    "management",
    "batchRecipients",
    id,
    filters,
  ],
};

export const useGetBulkSummation = (options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.bulkSummation(),
    queryFn: bulkUploadsService.getBulkSummation,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

export const useGetManagementBulkSummation = (options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.managementBulkSummation(),
    queryFn: bulkUploadsService.getManagementBulkSummation,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

export const useGetBatchHistory = (filters = {}, options = {}) => {
  const {
    searchQuery = "",
    status = "",
    startDate = "",
    endDate = "",
    page = 0,
    size = 10,
  } = filters;

  const activeFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      status,
      startDate,
      endDate,
      page,
      size,
    }).filter(([_, v]) => v !== "" && v != null),
  );
  return useQuery({
    queryKey: bulkUploadsQueryKeys.batchHistory(activeFilters),
    queryFn: () => bulkUploadsService.getBatchHistory(activeFilters),
    ...QUERY_CONFIGS.PAGINATED,
    ...options,
  });
};

export const useGetManagementBatchHistory = (filters = {}, options = {}) => {
  const {
    searchQuery = "",
    status = "",
    startDate = "",
    endDate = "",
    merchantId = "",
    page = 0,
    size = 10,
  } = filters;

  const activeFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      status,
      startDate,
      endDate,
      merchantId,
      page,
      size,
    }).filter(([_, v]) => v !== "" && v != null),
  );
  return useQuery({
    queryKey: bulkUploadsQueryKeys.managementBatchHistory(activeFilters),
    queryFn: () => bulkUploadsService.getManagementBatchHistory(activeFilters),
    ...QUERY_CONFIGS.PAGINATED,
    ...options,
  });
};

export const useGetBatchDetail = (id, options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.batchDetail(id),
    queryFn: () => bulkUploadsService.getBatchDetail(id),
    enabled: !!id,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

export const usePollBatchDetail = (id, { enabled = false } = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.batchValidationPoll(id),
    queryFn: () => bulkUploadsService.getBatchDetail(id),
    enabled: enabled && !!id,
    staleTime: 0,
    retry: false,
    refetchInterval: POLL_INTERVAL_MS,
  });
};

export const useGetManagementBatchDetail = (id, options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.managementBatchDetail(id),
    queryFn: () => bulkUploadsService.getManagementBatchDetail(id),
    enabled: !!id,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

export const useExportBatchHistory = (filters = {}) => {
  return useMutation({
    mutationFn: () => bulkUploadsService.exportBatchHistory(filters),
  });
};

export const useGetBatchEvents = (id, options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.batchEvents(id),
    queryFn: () => bulkUploadsService.getBatchEvents(id),
    enabled: !!id,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

export const useGetManagementBatchEvents = (id, options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.managementBatchEvents(id),
    queryFn: () => bulkUploadsService.getManagementBatchEvents(id),
    enabled: !!id,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

export const useExportBatchTransactions = (bulkId, filters = {}) => {
  return useMutation({
    mutationFn: () => bulkUploadsService.exportBatchRecipients(bulkId, filters),
  });
};

export const useGetBatchRecipients = (bulkId, filters = {}, options = {}) => {
  const {
    searchQuery = "",
    status = "",
    startDate = "",
    endDate = "",
    page = 0,
    size = 10,
  } = filters;

  const activeFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      status,
      startDate,
      endDate,
      page,
      size,
    }).filter(([_, v]) => v !== "" && v != null),
  );

  return useQuery({
    queryKey: bulkUploadsQueryKeys.batchRecipients(bulkId, activeFilters),
    queryFn: () => bulkUploadsService.getBatchRecipients(bulkId, activeFilters),
    enabled: !!bulkId,
    ...QUERY_CONFIGS.PAGINATED,
    ...options,
  });
};

export const useGetBatchRecipientsSummation = (bulkId, options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.batchRecipientsSummation(bulkId),
    queryFn: () => bulkUploadsService.getBatchRecipients(bulkId),
    enabled: !!bulkId,
    ...QUERY_CONFIGS.PAGINATED,
    ...options,
  });
};

export const useGetBatchValidationRecipients = (
  bulkId,
  filters = {},
  options = {},
) => {
  const {
    searchQuery = "",
    status = "",
    startDate = "",
    endDate = "",
    page = 0,
    size = 10,
  } = filters;

  const activeFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      status,
      startDate,
      endDate,
      page,
      size,
    }).filter(([_, v]) => v !== "" && v != null),
  );

  return useQuery({
    queryKey: bulkUploadsQueryKeys.batchValidationRecipients(
      bulkId,
      activeFilters,
    ),
    queryFn: () => bulkUploadsService.getBatchRecipients(bulkId, activeFilters),
    enabled: !!bulkId,
    ...QUERY_CONFIGS.PAGINATED,
    ...options,
  });
};

export const useGetManagementBatchRecipients = (
  bulkId,
  filters = {},
  options = {},
) => {
  const {
    searchQuery = "",
    status = "",
    startDate = "",
    endDate = "",
    page = 0,
    size = 10,
  } = filters;

  const activeFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      status,
      startDate,
      endDate,
      page,
      size,
    }).filter(([_, v]) => v !== "" && v != null),
  );

  return useQuery({
    queryKey: bulkUploadsQueryKeys.managementBatchRecipients(
      bulkId,
      activeFilters,
    ),
    queryFn: () =>
      bulkUploadsService.getManagementBatchRecipients(bulkId, activeFilters),
    enabled: !!bulkId,
    ...QUERY_CONFIGS.PAGINATED,
    ...options,
  });
};

export const useGetDisbursementLimits = (
  paymentChannel,
  isSuperAdmin = false,
) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.disbursementLimits(paymentChannel),
    queryFn: () => bulkUploadsService.getDisbursementLimits(paymentChannel),
    enabled: !!paymentChannel && !isSuperAdmin,
    staleTime: Infinity,
  });
};

export const useGetManualEntryCharges = () => {
  return useMutation({
    mutationFn: (payload) => bulkUploadsService.getDisbursementCharges(payload),
    mutationKey: bulkUploadsQueryKeys.manualEntryCharges(),
  });
};

export const useCreateManualEntryBatch = () => {
  return useMutation({
    mutationFn: (payload) => bulkUploadsService.createBulkDisbursement(payload),
    mutationKey: bulkUploadsQueryKeys.createManualEntryBatch(),
  });
};

export const useHakikisha = () => {
  return useMutation({
    mutationFn: (payload) => bulkUploadsService.hakikisha(payload),
    mutationKey: bulkUploadsQueryKeys.hakikisha(),
  });
};

export const useDownloadTemplateMutation = () => {
  return useMutation({
    mutationFn: bulkUploadsService.downloadTemplate,
  });
};

export const useUploadBulkFile = () => {
  return useMutation({
    mutationFn: (formData) => bulkUploadsService.uploadBulkFile(formData),
    mutationKey: bulkUploadsQueryKeys.uploadBulkFile(),
  });
};

export const useUpdateBulkRecipient = () => {
  return useMutation({
    mutationFn: ({ disbursementId, recipientId, payload }) =>
      bulkUploadsService.updateBulkRecipient(
        disbursementId,
        recipientId,
        payload,
      ),
    mutationKey: bulkUploadsQueryKeys.updateBulkRecipient(),
  });
};

export const useApproveBulkBatch = () => {
  return useMutation({
    mutationFn: (disbursementId) =>
      bulkUploadsService.approveBulkBatch(disbursementId),
    mutationKey: bulkUploadsQueryKeys.approveBulkBatch(),
  });
};

export const useGetBulkDisbursementStatusFilters = (options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.bulkStatusFilters(),
    queryFn: bulkUploadsService.getBulkDisbursementStatusFilters,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

export const useGetBulkDisbursementRecipientStatusFilters = (options = {}) => {
  return useQuery({
    queryKey: bulkUploadsQueryKeys.bulkRecipientStatusFilters(),
    queryFn: bulkUploadsService.getBulkDisbursementRecipientStatusFilters,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};
